import { PropsWithChildren, FC } from 'react'
import { Col } from 'antd'
import { MeetingDashboardFragment, MeetingStatus, MeetingRequestDto } from '../../../../types'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../../context/auth-context'
import { PerSlickSlider } from '../../../../components/custom_components/PerSlider'
import { useDateTime } from '../../../../shared/useDateTime'
import { EmployeeMeetingTile } from './EmployeeMeetingTile'
import { InviteEmployeeMeetingTile } from './InviteEmployeeMeetingTile'
import { RequestMeetingTile } from './RequestMeetingTile'
import { RequestedMeetingTile } from './RequestedMeetingTile'
import PerIcon from '../../../../components/custom_components/Icon'
import PerText from '../../../../components/custom_components/Text'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { dateSorter } from '../../../../shared/sortUtils'

type NextMeetingType = {
  meetingData: MeetingDashboardFragment[]
  requestData?: MeetingRequestDto[]
  employeeView?: boolean
  withManagerOptions?: boolean
  employeeId?: string
  editable?: boolean
  employeeName?: string
  isManager?: boolean
}

export const NextMeetingTile: FC<PropsWithChildren<NextMeetingType>> = ({
  meetingData,
  requestData,
  employeeView,
  withManagerOptions,
  employeeId,
  editable = false,
  employeeName,
  isManager,
}) => {
  const { user } = useAuth()
  const [t] = useTranslation()
  const { utcDateStringToMoment } = useDateTime(user)
  const isPrivateProfile = employeeId ? user.employeeId === employeeId : true
  const showInviteButton = withManagerOptions && editable
  const countMeetingData = meetingData.length

  const sortMeetingData = meetingData
    .filter((meeting) => meeting.status !== MeetingStatus.Canceled)
    .sort((a, b) => dateSorter(utcDateStringToMoment(a.displayDate.valueOf()), utcDateStringToMoment(b.displayDate.valueOf())))
  const isMeetingScheduledOrRequested = sortMeetingData.length > 0 || (requestData && requestData.length > 0)

  const inviteSingleEmployeeButton = (position: number) => {
    return (
      <Col span={8} key={position} data-e2e-meetings-item={position}>
        <InviteEmployeeMeetingTile employeeId={employeeId!} />
      </Col>
    )
  }

  const requestMeetingButton = (position: number) => {
    return (
      <Col span={8} key={position} data-e2e-meetings-item={position}>
        <RequestMeetingTile />
      </Col>
    )
  }

  return (
    <>
      {isMeetingScheduledOrRequested ? (
        <PerSlickSlider slidesToShow={3} className="slick-flex slides-align-end">
          {sortMeetingData.map((meeting, i: number) => (
            <Col span={showInviteButton ? 6 : 8} key={i} data-e2e-meetings-item={i}>
              <EmployeeMeetingTile user={user} meeting={meeting} employeeView={employeeView} withManagerOptions={withManagerOptions} />
            </Col>
          ))}
          {requestData &&
            requestData.map((request: MeetingRequestDto, i: number) => (
              <Col span={showInviteButton ? 6 : 8} key={`request_${i}`} data-e2e-meetings-item={`request_${i}`}>
                <RequestedMeetingTile
                  request={request}
                  isManager={request.reviewer.id !== user.employeeId && request.requester.id !== user.employeeId}
                  isReviewer={request.reviewer.id === user.employeeId}
                />
              </Col>
            ))}
          {showInviteButton && inviteSingleEmployeeButton(countMeetingData)}
          {isPrivateProfile && requestMeetingButton(countMeetingData)}
        </PerSlickSlider>
      ) : (
        <>
          <Col span={8}>
            <PerFlexRow align="middle" justify="space-between" noWrap>
              <PerIcon name="chatNo_stroke" size={isMeetingScheduledOrRequested ? 72 : 80} color="secondary" variant="decoration" />
              <PerText ml="m">
                {isManager
                  ? t('nextMeetingTile.noMeetingScheduledManager')
                  : withManagerOptions
                  ? t('nextMeetingTile.noMeetingScheduledEmployee', { employee: employeeName })
                  : t('nextMeetingTile.noMeetingScheduled')}
              </PerText>
            </PerFlexRow>
          </Col>
          {showInviteButton && inviteSingleEmployeeButton(countMeetingData)}
          {isPrivateProfile && requestMeetingButton(countMeetingData)}
        </>
      )}
    </>
  )
}
